import {
  Button,
  Icons,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiCaretLeft, PiCaretRight } from 'react-icons/pi';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useGPSLostAddGridToMap } from '../GpsLost/useGPSLostAddGridToMap';
import { useGPSLost } from '../GpsLost/GPSLostContext';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { useLayers } from '@ais-ui/react-openlayers';
import { PeriodNavigationActions } from './PeriodNavigationActions';

interface GPSLostActionsProps {
  setDisableDragNnDropTooltip: React.Dispatch<React.SetStateAction<boolean>>;
  isDragging: boolean;
  variant?: 'external' | 'inline';
}

export const GPSLostActions = ({
  setDisableDragNnDropTooltip,
  isDragging,
  variant = 'external',
}: GPSLostActionsProps) => {
  const {
    selectedGPSLostConfiguration,
    selectedInstanceIndex,
    setSelectedInstanceIndex,
    instances,
  } = useGPSLost();
  const { addGridToMap, isAdding } = useGPSLostAddGridToMap();
  const [lastClicked, setLastClicked] = useState<string | null>(null);

  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  const onNext = async () => {
    if (selectedInstanceIndex >= instances.length - 1) return;

    const instance = instances[selectedInstanceIndex + 1];
    await addGridToMap(selectedGPSLostConfiguration, instance, false);
    setSelectedInstanceIndex(selectedInstanceIndex + 1);
  };

  const onPrevious = async () => {
    if (selectedInstanceIndex <= 0) return;

    const instance = instances[selectedInstanceIndex - 1];
    await addGridToMap(selectedGPSLostConfiguration, instance, false);
    setSelectedInstanceIndex(selectedInstanceIndex - 1);
  };

  const isPreviousDisabled = selectedInstanceIndex <= 0;
  const isNextDisabled = selectedInstanceIndex >= instances.length - 1;

  const nextInstance = useMemo(() => {
    return selectedInstanceIndex < instances.length - 1
      ? instances[selectedInstanceIndex + 1]
      : null;
  }, [selectedInstanceIndex, instances]);

  const previousInstance = useMemo(() => {
    return selectedInstanceIndex > 0
      ? instances[selectedInstanceIndex - 1]
      : null;
  }, [selectedInstanceIndex, instances]);

  const getTimerangeDisplay = (
    timerange: { lower: Date; upper: Date } | undefined
  ): string => {
    if (!timerange || !timerange.lower || !timerange.upper)
      return 'Unknown timerange';

    const lowerDate = formatDateTimeWithTimeZone(timerange.lower);
    const upperDate = formatDateTimeWithTimeZone(timerange.upper);

    return `${lowerDate} - ${upperDate}`;
  };

  const config = {
    onNext,
    onPrevious,
    isPreviousDisabled,
    isNextDisabled,
    isLoading: isAdding,
    lastClicked,
    setLastClicked,
    previousTooltip: previousInstance
      ? `Switch to GPS Lost for period ${getTimerangeDisplay(
          previousInstance.timerange
        )}`
      : 'Switch to previous GPS Lost period',
    nextTooltip: nextInstance
      ? `Switch to GPS Lost for period ${getTimerangeDisplay(
          nextInstance.timerange
        )}`
      : 'Switch to next GPS Lost period',
    shouldShow: instances.length > 0,
  };

  return (
    <PeriodNavigationActions
      setDisableDragNnDropTooltip={setDisableDragNnDropTooltip}
      isDragging={isDragging}
      variant={variant}
      config={config}
    />
  );
};
